/**
 * @fileoverview React Hook for Activity Tracking Integration
 *
 * This module provides a React hook that integrates with the ActivityTracker utility
 * to track user activities across the application. It handles user authentication state,
 * organization context, and provides a clean API for tracking various user interactions.
 *
 * Key Features:
 * - Automatic initialization and cleanup based on authentication state
 * - Safe handling of user context changes and logout scenarios
 * - Prevention of anonymous activity tracking
 * - Batch processing of activity logs for optimal performance
 * - Integration with organization-specific API endpoints
 *
 * @requires ActivityTracker - The core activity tracking utility
 * @requires useAuth - Authentication context for user state
 * @requires useOrganization - Organization context for API configuration
 *
 * @example
 * // Basic usage
 * const { trackPageView, trackClick, trackFormSubmit } = useActivityTracker();
 *
 * // With custom options
 * const tracker = useActivityTracker({
 *   enabled: true,
 *   batchSize: 20,
 *   batchInterval: 45000
 * });
 *
 * // Track a page view
 * useEffect(() => {
 *   tracker.trackPageView(window.location.pathname, document.title);
 * }, [location]);
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import ActivityTracker from '../utils/ActivityTracker';

/**
 * Custom React hook for activity tracking integration
 *
 * Provides a clean interface for tracking user activities while handling
 * authentication state, organization context, and lifecycle management.
 * Automatically prevents anonymous tracking and ensures proper cleanup.
 *
 * @param {Object} [options={}] - Configuration options for the activity tracker
 * @param {boolean} [options.enabled=true] - Whether activity tracking is enabled
 * @param {number} [options.batchSize=10] - Number of activities to batch before sending
 * @param {number} [options.batchInterval=30000] - Interval in ms to send batched activities
 * @param {string} [options.apiEndpoint] - Custom API endpoint (defaults to org-specific endpoint)
 *
 * @returns {Object} Activity tracker interface
 * @returns {ActivityTracker|null} returns.tracker - The underlying ActivityTracker instance
 * @returns {Function} returns.trackPageView - Function to track page views
 * @returns {Function} returns.trackClick - Function to track click events
 * @returns {Function} returns.trackFormSubmit - Function to track form submissions
 * @returns {Function} returns.trackApiCall - Function to track API calls
 * @returns {Function} returns.logSystemEvent - Function to log custom system events
 * @returns {Function} returns.enable - Function to enable activity tracking
 * @returns {Function} returns.disable - Function to disable activity tracking
 * @returns {boolean} returns.isEnabled - Current enabled state of the tracker
 *
 * @example
 * const {
 *   trackPageView,
 *   trackClick,
 *   trackFormSubmit,
 *   trackApiCall,
 *   logSystemEvent,
 *   isEnabled
 * } = useActivityTracker({
 *   enabled: true,
 *   batchSize: 15,
 *   batchInterval: 45000
 * });
 */
export const useActivityTracker = (options = {}) => {
  const { user } = useAuth();
  const { getApiUrl } = useOrganization();
  const trackerRef = useRef(null);
  const optionsRef = useRef(options);
  
  // Update options ref when options change
  optionsRef.current = options;
  
  const defaultOptions = useRef({ // eslint-disable-line no-unused-vars
    enabled: true,
    apiEndpoint: `${getApiUrl()}/api/activity-logs`,
    batchSize: 10,
    batchInterval: 30000, // 30 seconds
    ...options
  });

  // Single effect for initialization and user context management
  useEffect(() => {
    const apiUrl = getApiUrl();

    const currentOptions = {
      enabled: true,
      apiEndpoint: `${apiUrl}/api/activity-logs`,
      batchSize: 10,
      batchInterval: 30000, // 30 seconds
      ...optionsRef.current
    };

    // Case 1: User logged out - destroy existing tracker
    if (!user && trackerRef.current) {
      // console.log('User logged out - destroying ActivityTracker');
      trackerRef.current.destroy();
      trackerRef.current = null;
      return;
    }

    // Case 2: User exists but has no ID - destroy tracker to prevent NULL logging
    if (user && !user.id && trackerRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.error('User exists but has no ID - destroying tracker to prevent NULL activity logging!');
      }
      trackerRef.current.destroy();
      trackerRef.current = null;
      return;
    }

    // Case 3: Initialize tracker if enabled and we have a valid user (avoid duplicates)
    if (currentOptions.enabled && !trackerRef.current && user && user.id) {
      // console.log('Initializing ActivityTracker for user:', user.id, 'org:', user.organization_id);
      trackerRef.current = new ActivityTracker(currentOptions);
      trackerRef.current.setUser(user.id, user.organization_id);
      return;
    }

    // Case 4: Update existing tracker user context (user org changed)
    if (trackerRef.current && user && user.id) {
      // console.log('Updating ActivityTracker user context:', user.id, 'org:', user.organization_id);
      trackerRef.current.setUser(user.id, user.organization_id);
    }

    // Cleanup on unmount
    return () => {
      if (trackerRef.current) {
        // console.log('Component unmounting - cleaning up ActivityTracker');
        trackerRef.current.destroy();
        trackerRef.current = null;
      }
    };
  }, [user?.id, user?.organization_id, getApiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose tracker methods with useCallback to prevent infinite re-renders
  /**
   * Tracks a page view event
   *
   * @param {string} url - The URL of the page being viewed
   * @param {string} [title] - The title of the page
   * @param {string} [referrer] - The referrer URL
   *
   * @example
   * trackPageView('/dashboard', 'Dashboard', '/login');
   */
  const trackPageView = useCallback((url, title, referrer) => {
    if (trackerRef.current) {
      trackerRef.current.trackPageView(url, title, referrer);
    }
  }, []);

  /**
   * Tracks a click event
   *
   * @param {Event} event - The click event object
   *
   * @example
   * <button onClick={trackClick}>Click me</button>
   */
  const trackClick = useCallback((event) => {
    if (trackerRef.current) {
      trackerRef.current.trackClick(event);
    }
  }, []);

  /**
   * Tracks a form submission event
   *
   * @param {HTMLFormElement|string} form - The form element or form identifier
   * @param {boolean} success - Whether the form submission was successful
   * @param {Object} [errorDetails] - Details about any errors that occurred
   *
   * @example
   * trackFormSubmit('login-form', true);
   * trackFormSubmit(formElement, false, { error: 'Validation failed' });
   */
  const trackFormSubmit = useCallback((form, success, errorDetails) => {
    if (trackerRef.current) {
      trackerRef.current.trackFormSubmit(form, success, errorDetails);
    }
  }, []);

  /**
   * Tracks an API call event
   *
   * @param {string} endpoint - The API endpoint that was called
   * @param {string} method - The HTTP method used (GET, POST, etc.)
   * @param {number} responseTime - The response time in milliseconds
   * @param {number} statusCode - The HTTP status code returned
   * @param {Object} [errorDetails] - Details about any errors that occurred
   *
   * @example
   * trackApiCall('/api/users', 'GET', 250, 200);
   * trackApiCall('/api/login', 'POST', 1500, 400, { error: 'Invalid credentials' });
   */
  const trackApiCall = useCallback((endpoint, method, responseTime, statusCode, errorDetails) => {
    if (trackerRef.current) {
      trackerRef.current.trackApiCall(endpoint, method, responseTime, statusCode, errorDetails);
    }
  }, []);

  /**
   * Logs a custom system event
   *
   * @param {string} eventName - The name of the event
   * @param {Object} [eventData] - Additional data associated with the event
   *
   * @example
   * logSystemEvent('user_preference_changed', { setting: 'theme', value: 'dark' });
   * logSystemEvent('feature_used', { feature: 'advanced_search' });
   */
  const logSystemEvent = useCallback((eventName, eventData) => {
    if (trackerRef.current) {
      trackerRef.current.logSystemEvent(eventName, eventData);
    }
  }, []);

  /**
   * Enables activity tracking
   *
   * @example
   * enable();
   */
  const enable = useCallback(() => {
    if (trackerRef.current) {
      trackerRef.current.enable();
    }
  }, []);

  /**
   * Disables activity tracking
   *
   * @example
   * disable();
   */
  const disable = useCallback(() => {
    if (trackerRef.current) {
      trackerRef.current.disable();
    }
  }, []);

  return {
    tracker: trackerRef.current,
    trackPageView,
    trackClick,
    trackFormSubmit,
    trackApiCall,
    logSystemEvent,
    enable,
    disable,
    isEnabled: trackerRef.current?.enabled || false
  };
};

export default useActivityTracker;